<script lang="ts" setup>
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import type { ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useAppEmit from '@lib/composables/appEmit.use'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasExit from '@canvas-builder/composables/useCanvasExit'
import { CanvasBuilderMode } from '@canvas-builder/types/canvas'
import useNavigatorDrawer from '@canvas-builder/composables/useNavigatorDrawer'
import Navigator from '@canvas-builder/components/container/Navigator/Navigator.vue'
import InstalledEmbeddableShortcuts from '@canvas-builder/components/container/ControlBar/InstalledEmbeddableShortcuts.vue'
import PopupAppsButtons from '@canvas-builder/components/container/PopupApps/PopupAppsButtons.vue'
import AnnotationMenu from '@canvas-builder/components/container/AnnotationMenu/AnnotationMenu.vue'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'

defineOptions({
  inheritAttrs: false,
})

const { t } = useI18n()
const { appEmit } = useAppEmit()
const {
  goToNextPage,
  goToPreviousPage,
  isFirstPage,
  isLastPage,
  backToPreviousLocation,
  isBackToPreviousLocationVisible,
  isBackToPreviousLocationDisabled,
} = useCanvasPages()
const {
  mode: activeMode,
  exitFullscreen,
  navigateToHome,
  isCanvas,
  isCanvasTemplate,
  isCanvasPreviewMode,
  isEditMode,
  isDsr,
} = useCanvas()

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const { toggleNavigatorDrawer, isNavigatorDrawerOpen } = useNavigatorDrawer()
const { onExitClick } = useCanvasExit()
const stateWhenOpened = ref(false)
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const showPageButtons = computed(
  () => (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') !== 'collapsibles',
)
const showNotesButton = computed(
  () => launchDarkly.value.allow_note_taking && activeMode.value === CanvasBuilderMode.IMPACT,
)

onMounted(() => {
  stateWhenOpened.value = isNavigatorDrawerOpen.value
  isNavigatorDrawerOpen.value = false
})

onUnmounted(() => {
  isNavigatorDrawerOpen.value = stateWhenOpened.value
})
</script>

<template>
  <div v-if="isNavigatorDrawerOpen" class="fixed top-2 left-2 bottom-20 z-11">
    <Navigator class="b-rounded-2 opacity-100 shadow-md" is-fullscreen-navigator />
  </div>
  <div v-bind="$attrs" class="flex items-center opacity-70">
    <div class="flex-1 flex justify-start">
      <CButton
        v-if="showPageButtons && !isFirstPage"
        v-tooltip="t('canvasUI.canvasBuilder.components.previousPage')"
        class="h-14 w-11"
        secondary
        size="large"
        strong
        @click="goToPreviousPage"
      >
        <template #icon>
          <CIcon icon="left" size="18" />
        </template>
      </CButton>
    </div>

    <div v-if="!isCanvasPreviewMode" class="flex-1 gap-2 flex justify-center">
      <CButton
        v-if="activeMode !== CanvasBuilderMode.SHARING"
        v-tooltip="t('canvasUI.canvasBuilder.components.exit')"
        circle
        secondary
        size="large"
        strong
        @click="onExitClick"
      >
        <template #icon>
          <CIcon class="fa-flip-horizontal" icon="right-from-bracket" size="18" />
        </template>
      </CButton>
      <CButton
        v-if="isCanvas || isCanvasTemplate"
        v-tooltip="t('canvasUI.canvasBuilder.components.home')"
        circle
        secondary
        size="large"
        strong
        @click="navigateToHome()"
      >
        <template #icon>
          <CIcon icon="home" size="18" />
        </template>
      </CButton>
      <CButton
        v-tooltip="t('canvasUI.canvasBuilder.components.toc')"
        circle
        secondary
        size="large"
        strong
        @click="toggleNavigatorDrawer"
      >
        <template #icon>
          <CIcon icon="sidebar" size="18" />
        </template>
      </CButton>

      <AnnotationMenu v-if="pitcherSettings.enable_presentation_annotations && !isDsr && !isEditMode" circle />

      <CButton
        v-if="isBackToPreviousLocationVisible"
        v-tooltip="
          isBackToPreviousLocationDisabled
            ? t('canvasUI.canvasBuilder.components.backToPreviousLocationDisabled')
            : t('canvasUI.canvasBuilder.components.backToPreviousLocation')
        "
        circle
        :disabled="isBackToPreviousLocationDisabled"
        secondary
        size="large"
        strong
        @click="backToPreviousLocation"
      >
        <template #icon>
          <CIcon icon="backward" size="18" />
        </template>
      </CButton>

      <CButton
        v-tooltip="t('canvasUI.canvasBuilder.components.fullscreenExit')"
        circle
        secondary
        size="large"
        strong
        @click="exitFullscreen"
      >
        <template #icon>
          <CIcon icon="compress" size="18" />
        </template>
      </CButton>
      <CButton
        v-if="showNotesButton"
        v-tooltip="t('canvasUI.canvasBuilder.components.notes')"
        circle
        secondary
        size="large"
        strong
        @click="appEmit('toggleNotes')"
      >
        <template #icon>
          <CIcon icon="note-sticky" size="18" />
        </template>
      </CButton>
      <InstalledEmbeddableShortcuts />
      <PopupAppsButtons button-size="large" inline is-circle placement="top" />
    </div>
    <div class="flex-1 flex justify-end">
      <CButton
        v-if="showPageButtons && !isLastPage"
        v-tooltip="t('canvasUI.canvasBuilder.components.nextPage')"
        class="h-14 w-11"
        secondary
        size="large"
        strong
        @click="goToNextPage"
      >
        <template #icon>
          <CIcon icon="right" size="18" />
        </template>
      </CButton>
    </div>
  </div>
</template>
